import { defaultAgentImageList } from "@jugl-web/domain-resources/chat-bot/utils/defaultAgentImages/defaultAgentImages";
import { useFileSelect } from "@jugl-web/domain-resources/files/hooks/useFileSelect";
import {
  Dialog,
  InteractiveContainer,
  PlainButton,
  CropImageModal,
} from "@jugl-web/ui-kit";
import { Button } from "@jugl-web/ui-kit/primitives/Button";
import {
  cx,
  isFileTypeAccepted,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as GearsIcon } from "./assets/gears.svg";
import { EditableMediaContainer } from "../../../../components/EditableMediaContainer";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";

interface SelectAgentAvatarDialogProps {
  isOpen: boolean;
  selectedAvatar: string | File | null;
  onAvatarChange: (avatar: string | File) => void;
  onClose: () => void;
}

const AVATAR_TILE_CLASS_NAME =
  "relative min-w-0 aspect-[3/2] w-full overflow-hidden rounded-xl border-4 border-solid border-transparent transition-all";

const CUSTOM_MEDIA_TILE_CLASS_NAME = `${AVATAR_TILE_CLASS_NAME} flex items-center justify-center bg-[#FAFAFA] hover:border-dark-100`;

export const SelectAgentAvatarDialog: FC<SelectAgentAvatarDialogProps> = ({
  isOpen,
  selectedAvatar,
  onAvatarChange,
  onClose,
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const [internalSelectedAvatar, setInternalSelectedAvatar] = useState<
    string | File | null
  >(selectedAvatar);

  const [previousCustomAvatar, setPreviousCustomAvatar] = useState<
    string | File | null
  >(null);

  const customAvatarUrlRef = useRef<string | null>(null);
  const previousUrlRef = useRef<string | null>(null);

  const customAvatarSrc = useMemo(() => {
    if (internalSelectedAvatar) {
      if (internalSelectedAvatar instanceof File) {
        const url = URL.createObjectURL(internalSelectedAvatar);
        customAvatarUrlRef.current = url;
        return url;
      }

      if (!internalSelectedAvatar.startsWith("default")) {
        return internalSelectedAvatar;
      }
    }

    return null;
  }, [internalSelectedAvatar]);

  const previousCustomAvatarSrc = useMemo(() => {
    if (previousCustomAvatar instanceof File) {
      const url = URL.createObjectURL(previousCustomAvatar);
      previousUrlRef.current = url;
      return url;
    }

    if (
      typeof previousCustomAvatar === "string" &&
      !previousCustomAvatar.startsWith("default")
    ) {
      return previousCustomAvatar;
    }

    return null;
  }, [previousCustomAvatar]);

  const hasPreviousCustomAvatar = !!previousCustomAvatarSrc && !customAvatarSrc;

  const { selectFile } = useFileSelect();

  const { t } = useTranslations();
  const { toast } = useToast();

  const handleFileChange = useCallback(async () => {
    const selectedFiles = await selectFile({
      acceptTypes: "image/*",
      maxFileSize: 1048576,
      multiple: false,
    });

    const file = selectedFiles?.[0];

    if (file) {
      if (!isFileTypeAccepted(file, "image/*")) {
        toast(
          t({
            id: "cx-ai-agents-page.incorrect-file-format",
            defaultMessage: "Incorrect file format ❌",
          }),
          { variant: "error" }
        );
        return;
      }

      setInternalSelectedAvatar(file);
      setIsCropModalOpen(true);
    }
  }, [selectFile, t, toast]);

  const handleDefaultAvatarSelect = useCallback(
    (avatarId: string) => {
      if (customAvatarSrc) {
        setPreviousCustomAvatar(internalSelectedAvatar);
      }
      setInternalSelectedAvatar(avatarId);
    },
    [customAvatarSrc, internalSelectedAvatar]
  );

  // Revoke created URLs
  useEffect(
    () => () => {
      if (previousUrlRef.current) {
        URL.revokeObjectURL(previousUrlRef.current);
      }

      if (customAvatarUrlRef.current) {
        URL.revokeObjectURL(customAvatarUrlRef.current);
      }
    },
    []
  );

  // Reset internal state when external prop changes
  useEffect(() => {
    if (isOpen) {
      setInternalSelectedAvatar(selectedAvatar);
    }
  }, [selectedAvatar, isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      className="flex w-full max-w-[780px] flex-col font-secondary"
    >
      <div className="flex h-[60px] w-full shrink-0 items-center justify-between border-0 border-b border-solid border-dark-100 px-6">
        <div className="flex items-center gap-2 font-semibold text-[#383838]">
          <GearsIcon />
          {t({
            id: "agent-avatar-select.agent-picture",
            defaultMessage: "Agent Picture",
          })}
        </div>
        <PlainButton className="cursor-pointer rounded-lg hover:bg-grey-100">
          <CloseIcon onClick={onClose} />
        </PlainButton>
      </div>
      <div className="jugl__custom-scrollbar overflow-y-auto">
        {/* Default avatars */}
        <div className="mx-auto grid w-full max-w-[610px] grid-cols-2 gap-4 px-6 pt-12 sm:grid-cols-3 sm:gap-x-[35px] sm:gap-y-5">
          {defaultAgentImageList.map((avatarData) => (
            <InteractiveContainer
              key={avatarData.id}
              onClick={() => handleDefaultAvatarSelect(avatarData.id)}
              className={cx(
                AVATAR_TILE_CLASS_NAME,
                internalSelectedAvatar === avatarData.id
                  ? "border-dark"
                  : "hover:border-dark-100"
              )}
            >
              <img
                src={avatarData.src}
                alt="Agent Avatar"
                className="h-full w-full object-cover"
              />
            </InteractiveContainer>
          ))}
          {/* Custom avatar */}
          <div className="col-span-2 grid grid-cols-2 items-center gap-4 sm:gap-[35px]">
            {customAvatarSrc || previousCustomAvatarSrc ? (
              <EditableMediaContainer
                onSelectNew={handleFileChange}
                onRemove={() => {
                  setPreviousCustomAvatar(null);
                  if (customAvatarSrc) {
                    setInternalSelectedAvatar(null);
                  }
                }}
              >
                <InteractiveContainer
                  onClick={
                    hasPreviousCustomAvatar
                      ? () => setInternalSelectedAvatar(previousCustomAvatar)
                      : undefined
                  }
                  isDisabled={!!customAvatarSrc}
                  className={cx(
                    CUSTOM_MEDIA_TILE_CLASS_NAME,
                    customAvatarSrc &&
                      "border-4 border-solid border-dark hover:border-dark"
                  )}
                >
                  <div className="relative h-full w-full">
                    <img
                      src={
                        (customAvatarSrc ?? previousCustomAvatarSrc) as string
                      }
                      alt="Uploaded Avatar"
                      className="h-full w-full rounded-xl object-cover"
                    />
                  </div>
                </InteractiveContainer>
              </EditableMediaContainer>
            ) : (
              <InteractiveContainer
                onClick={handleFileChange}
                className={CUSTOM_MEDIA_TILE_CLASS_NAME}
              >
                <div className="text-grey-400">
                  <PlusIcon />
                </div>
              </InteractiveContainer>
            )}
            <div className="min-w-0 text-sm text-dark-700">
              {t(
                {
                  id: "agent-avatar-select.upload-hint",
                  defaultMessage:
                    "Upload JPG, PNG, or SVG image, up to {maxSize} MB",
                },
                { maxSize: 1 }
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-12 flex h-[72px] w-full shrink-0 items-center justify-end border border-r-0 border-b-0 border-l-0 border-solid border-dark-100 px-6">
        <Button
          onClick={() => {
            if (internalSelectedAvatar !== null) {
              onAvatarChange(internalSelectedAvatar);
              onClose();
            }
          }}
          iconEnd={<ArrowRightIcon />}
          isDisabled={internalSelectedAvatar === null}
          className="w-full max-w-[300px]"
        >
          {t({
            id: "common.save",
            defaultMessage: "Save",
          })}
        </Button>
      </div>
      <CropImageModal
        isOpen={isCropModalOpen}
        image={
          internalSelectedAvatar instanceof File
            ? internalSelectedAvatar
            : undefined
        }
        backdropClassName="z-[2100]"
        panelClassName="z-[2200]"
        onClose={() => setIsCropModalOpen(false)}
        onChangeImage={handleFileChange}
        onSave={(image) => {
          setInternalSelectedAvatar(image);
          setIsCropModalOpen(false);
        }}
      />
    </Dialog>
  );
};
